import React, { useEffect, useState } from 'react';
import { Table, Button, Form, Input, InputNumber, message, Space, Popconfirm, Card, Row, Col, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import api from '../../services/api';
import SearchFilter from '../../components/SearchFilter';
import FormModal from '../../components/FormModal';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './Doctors.css';

const CompanySellers = () => {
    const [companySellers, setCompanySellers] = useState([]);
    const [filteredCompanySellers, setFilteredCompanySellers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingCompanySeller, setEditingCompanySeller] = useState(null);
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        fetchCompanySellers();
    }, []);

    const fetchCompanySellers = async () => {
        setLoading(true);
        try {
            const response = await api.get('/superadmin/companysellers');
            setCompanySellers(response.data.data);
            setFilteredCompanySellers(response.data.data);
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (values) => {
        let filtered = companySellers;

        if (values.name) {
            filtered = filtered.filter(cs =>
                cs.name.toLowerCase().includes(values.name.toLowerCase()) ||
                (cs.companyName && cs.companyName.toLowerCase().includes(values.name.toLowerCase()))
            );
        }

        if (values.phoneNumber) {
            filtered = filtered.filter(cs =>
                cs.phoneNumber.includes(values.phoneNumber)
            );
        }

        setFilteredCompanySellers(filtered);
    };

    const handleReset = () => {
        setFilteredCompanySellers(companySellers);
    };

    const handleCreate = () => {
        setEditingCompanySeller(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingCompanySeller(record);
        form.setFieldsValue(record);
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/superadmin/companyseller/${id}`);
            message.success(t('success'));
            fetchCompanySellers();
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        }
    };

    const handleSubmit = async (values) => {
        try {
            if (editingCompanySeller) {
                await api.put(`/superadmin/companyseller/${editingCompanySeller._id}`, values);
            } else {
                await api.post('/superadmin/create-companyseller', values);
            }
            message.success(t('success'));
            setModalVisible(false);
            form.resetFields();
            fetchCompanySellers();
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        }
    };

    const searchFields = [
        {
            name: 'name',
            label: t('name') || 'Name / Company',
            type: 'text',
            placeholder: t('enterName') || 'Enter name or company name'
        },
        {
            name: 'phoneNumber',
            label: t('phoneNumber') || 'Phone Number',
            type: 'text',
            placeholder: t('enterPhone') || 'Enter phone number'
        }
    ];

    const columns = [
        {
            title: t('name') || 'Contact Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name)
        },
        {
            title: t('companyName') || 'Company Name',
            dataIndex: 'companyName',
            key: 'companyName',
            sorter: (a, b) => (a.companyName || '').localeCompare(b.companyName || '')
        },
        {
            title: t('phoneNumber') || 'Phone Number',
            dataIndex: 'phoneNumber',
            key: 'phoneNumber'
        },
        {
            title: t('email') || 'Email',
            dataIndex: 'email',
            key: 'email'
        },
        {
            title: t('doctorLimit') || 'Doctor Limit',
            key: 'doctorLimit',
            render: (_, record) => (
                <Space>
                    <Tag color={record.doctorCount >= record.doctorLimit ? 'red' : 'green'}>
                        {record.doctorCount} / {record.doctorLimit}
                    </Tag>
                </Space>
            )
        },
        {
            title: t('remainingSlots') || 'Remaining',
            dataIndex: 'remainingSlots',
            key: 'remainingSlots',
            render: (slots) => (
                <Tag color={slots > 0 ? 'blue' : 'red'}>
                    {slots}
                </Tag>
            )
        },
        {
            title: t('status') || 'Status',
            key: 'isActive',
            render: (_, record) => (
                <Tag color={record.isActive ? 'green' : 'red'}>
                    {record.isActive ? t('active') || 'Active' : t('inactive') || 'Inactive'}
                </Tag>
            )
        },
        {
            title: t('actions') || 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 150,
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    <Popconfirm
                        title={t('confirmDelete') || 'Are you sure you want to delete this company seller?'}
                        onConfirm={() => handleDelete(record._id)}
                        okText={t('yes') || 'Yes'}
                        cancelText={t('no') || 'No'}
                    >
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div className="doctors-container">
            <Card>
                <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
                    <Col>
                        <h2>{t('companySellers') || 'Company Sellers'}</h2>
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleCreate}
                        >
                            {t('addCompanySeller') || 'Add Company Seller'}
                        </Button>
                    </Col>
                </Row>

                <SearchFilter
                    fields={searchFields}
                    onSearch={handleSearch}
                    onReset={handleReset}
                />

                <Table
                    columns={columns}
                    dataSource={filteredCompanySellers}
                    rowKey="_id"
                    loading={loading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `${t('total') || 'Total'} ${total} ${t('items') || 'items'}`
                    }}
                    scroll={{ x: 1200 }}
                />

                <FormModal
                    visible={modalVisible}
                    title={editingCompanySeller ? (t('editCompanySeller') || 'Edit Company Seller') : (t('addCompanySeller') || 'Add Company Seller')}
                    onCancel={() => {
                        setModalVisible(false);
                        form.resetFields();
                    }}
                    onSubmit={handleSubmit}
                    form={form}
                >
                    <Form.Item
                        name="name"
                        label={t('name') || 'Contact Name'}
                        rules={[{ required: true, message: t('pleaseEnterName') || 'Please enter name' }]}
                    >
                        <Input placeholder={t('enterName') || 'Enter contact name'} />
                    </Form.Item>

                    <Form.Item
                        name="companyName"
                        label={t('companyName') || 'Company Name'}
                        rules={[{ required: true, message: t('pleaseEnterCompanyName') || 'Please enter company name' }]}
                    >
                        <Input placeholder={t('enterCompanyName') || 'Enter company name'} />
                    </Form.Item>

                    <Form.Item
                        name="phoneNumber"
                        label={t('phoneNumber') || 'Phone Number'}
                        rules={[{ required: true, message: t('pleaseEnterPhone') || 'Please enter phone number' }]}
                    >
                        <Input placeholder={t('enterPhone') || 'Enter phone number'} />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label={t('email') || 'Email'}
                    >
                        <Input type="email" placeholder={t('enterEmail') || 'Enter email'} />
                    </Form.Item>

                    {!editingCompanySeller && (
                        <Form.Item
                            name="password"
                            label={t('password') || 'Password'}
                            rules={[{ required: true, message: t('pleaseEnterPassword') || 'Please enter password' }]}
                        >
                            <Input.Password placeholder="Enter password" />
                        </Form.Item>
                    )}

                    <Form.Item
                        name="doctorLimit"
                        label={t('doctorLimit') || 'Doctor Limit'}
                        rules={[{ required: true, message: t('pleaseEnterDoctorLimit') || 'Please enter doctor limit' }]}
                    >
                        <InputNumber min={1} style={{ width: '100%' }} placeholder="Enter maximum number of doctors" />
                    </Form.Item>

                    <Form.Item
                        name="address"
                        label={t('address') || 'Address'}
                    >
                        <Input.TextArea rows={2} placeholder={t('enterAddress') || 'Enter address'} />
                    </Form.Item>
                </FormModal>
            </Card>
        </div>
    );
};

export default CompanySellers;
